import { NetbankingAPI, debug, toJS } from "aaa";
import * as _ from "lodash";
import { action, observable, computed } from "mobx";
import { IOffer } from "types/offer";
import { IAccount } from "types/account";
import { NetbankingAPIClient } from "network/APIClient";

const availableChannelProducts = {
    // Master cards    
    "C-SKMCFIRSP": null,
    "C-SKMCGOLDP": null,
    "C-SKMCPLAT": null,
    // Visa cards
    "C-SKVIFIRS": null,
    "C-SKVIGOLD": null,
    "C-SKVIPLAT": null,
    // New Master cards
    "MASTERPREM": null,
    "MASTERSMAR": null,
    "MASTERSMAR_N2CC": null,
    "MASTERPREM_N2CC": null,
    // New Visa cards
    "VISAPREM": null,
    "VISAMETALL": null,
    "VISASMAR": null,
    "VISAPREM_N2CC": null,
    "VISASMAR_N2CC": null,
    // Prepaid cards
    "S-PREPAID-CLASSIC-CARD": null,
    "S-PREPAID-CLASSIC-ONLINE": null,
    "S-PREPAID-GIFT-CARD": null,
    "S-PREPAID-GIFT-ONLINE": null,
    // Bank cards
    "C-BKMOBIL": null,
    "C-BKDSTICKER": null,
    "C-BKDMICRO": null,
    // Salary advance
    "K-MASTER-VOR": null,
    "C-SKMCVORSCH": null,
    // Consumer loan
    "CONSUMER-LOAN": null,
    // Savings accounts 
    "S-PROFIT": null,
    "S-KAPITAL": null,
    "S-PRAEMIE": null,
    // Accident insurance
    "ACCIDENT_INSURANCE": null,
    // Life insurance
    "LIFE_INSURANCE": null,
    // Miles and More cards
    "MASTERPREB_MM": null,
    "MASTERSMAB_MM": null,
    // Junior products
    "SMARTJUNIOR": null,
    "GIROSPARK7": null,
    "GIROJUNIOR": null,
};

// Generate the type "C-SKMCFIRSP" | "C-SKMCGOLDP" ... to guard against typos for type safety
export type Product = keyof typeof availableChannelProducts;

export function isCreditCard(channelProduct: string) {
    const creditCards: Product[] = [
        "C-SKMCFIRSP",
        "C-SKMCGOLDP",
        "C-SKMCPLAT",
        "C-SKVIFIRS",
        "C-SKVIGOLD",
        "C-SKVIPLAT",
    ];

    return creditCards.indexOf(channelProduct as Product) >= 0;
}

export function isNewCreditCard(channelProduct: string) {
    const creditCards: Product[] = [
        "VISAMETALL",
        "VISAPREM",
        "VISASMAR",
        "MASTERSMAR",
        "MASTERPREM",
        "VISAPREM_N2CC",
        "VISASMAR_N2CC",
        "MASTERSMAR_N2CC",
        "MASTERPREM_N2CC",
        "MASTERPREB_MM",
        "MASTERSMAB_MM",
    ];

    return creditCards.indexOf(channelProduct as Product) >= 0;
}

export function isMasterCard(channelProduct: string) {
    const creditCards: Product[] = [
        "C-SKMCFIRSP",
        "C-SKMCGOLDP",
        "C-SKMCPLAT",
    ];

    return creditCards.indexOf(channelProduct as Product) >= 0;
}

export function isVisaCard(channelProduct: string) {
    const creditCards: Product[] = [
        "C-SKVIFIRS",
        "C-SKVIGOLD",
        "C-SKVIPLAT",
    ];

    return creditCards.indexOf(channelProduct as Product) >= 0;
}

export function isBankCard(channelProduct: string) {
    const bankCards: Product[] = [
        "C-BKMOBIL",
        "C-BKDSTICKER",
        "C-BKDMICRO",
    ];

    return bankCards.indexOf(channelProduct as Product) >= 0;
}

export function isSalaryAdvanceCard(channelProduct: string) {
    const salaryAdvance: Product[] = [
        "K-MASTER-VOR",
        "C-SKMCVORSCH"
    ];

    return salaryAdvance.indexOf(channelProduct as Product) >= 0;
}

export function isSavingsAccount(channelProduct: string) {
    const savings: Product[] = [
        "S-PROFIT",
        "S-KAPITAL",
        "S-PRAEMIE"
    ];

    return savings.indexOf(channelProduct as Product) >= 0;
}

export function isConsumerLoan(channelProduct: string) {
    const consumerLoan: Product[] = ["CONSUMER-LOAN"];
    return consumerLoan.indexOf(channelProduct as Product) >= 0;
}

export function isAccidentInsurance(channelProduct: string) {
    const accidentInsurance: Product[] = ["ACCIDENT_INSURANCE"];
    return accidentInsurance.indexOf(channelProduct as Product) >= 0;
}

export function isLifeInsurance(channelProduct: string) {
    const lifeInsurance: Product[] = ["LIFE_INSURANCE"];
    return lifeInsurance.indexOf(channelProduct as Product) >= 0;
}

export function isJuniorSmartSparen(channelProduct: string) {
    const juniorSmartSparen: Product[] = ["SMARTJUNIOR"];
    return juniorSmartSparen.indexOf(channelProduct as Product) >= 0;
}

export function isJuniorSpark7(channelProduct: string) {
    const juniorAccount: Product[] = ["GIROSPARK7"];
    return juniorAccount.indexOf(channelProduct as Product) >= 0;
}

export function isJuniorGiro(channelProduct: string) {
    const juniorGiro: Product[] = ["GIROJUNIOR"];
    return juniorGiro.indexOf(channelProduct as Product) >= 0;
}

export function isJuniorProduct(channelProduct: string) {
    const juniorProducts: Product[] = ["SMARTJUNIOR", "GIROSPARK7", "GIROJUNIOR"];
    return juniorProducts.indexOf(channelProduct as Product) >= 0;
}

export function isJuniorAccount(account: IAccount) {
    return account.type === "CURRENT" || account.type === "SAVING";
}

export class OffersStore {
    channelProducts?: string[];
    @observable offers?: IOffer[];

    @observable isLoading: boolean = false;

    constructor(channelProducts?: string[]) {
        this.channelProducts = channelProducts;
    }

    @action async load() {
        const target = NetbankingAPI.getOffers(this.channelProducts);
        this.isLoading = true;

        try {
            this.offers = await NetbankingAPIClient.requestType(target);
            debug.log("### offers", toJS(this.offers));
            this.isLoading = false;
        } catch (error) {
            debug.log("Error loading offers", error);
            this.isLoading = false;
            throw error;
        }
    }

    @action filterShopOffers() {
        if (this.offers && this.offers.length > 0) {
            this.offers = this.offers.filter(offer => {
                return _.includes(Object.keys(availableChannelProducts), offer.channelProduct);
            });
        }
    }

    findByChannelProduct(channelProduct: string): IOffer | undefined {
        return this.offers ? this.offers.find(offer => {
            return offer.channelProduct === channelProduct;
        }) : undefined;
    }

    findByProductId(productId: string, channelProduct: string): IOffer | undefined {
        return this.offers ? this.offers.find(offer => {
            return offer.productId === productId &&
                offer.channelProduct === channelProduct;
        }) : undefined;
    }

    findByProductIdAndConditionsGroup(productId: string, conditionsGroupId: string, channelProduct: string): IOffer | undefined {
        return this.offers ? this.offers.find(offer => {
            return offer.productId === productId &&
                offer.conditionsGroupId === conditionsGroupId &&
                offer.channelProduct === channelProduct;
        }) : undefined;
    }

    @computed get hasConsumerLoan() {
        const ret = this.findByChannelProduct("CONSUMER-LOAN") !== undefined;
        return ret;
    }
}



// WEBPACK FOOTER //
// ./src/stores/OffersStore.ts